import type { WebsiteT } from '../hooks/use-website-t';
import { isLegacyDesignLive, type ActiveEditorState } from './editor-state';

/**
 * Epic #228 P3: what a publish of this template does for shoppers.
 *
 * - `changes`: this template is live; its latest saved changes go live.
 * - `replace`: another template is live; this one replaces it.
 * - `replace-original`: a converted store still serves its original builder
 *   design (`isLegacyDesignLive`); this design replaces it.
 * - `first`: nothing is live yet, including a brand-new store (legacy mode,
 *   `conversion: 'none'`).
 */
export type PublishOutcome =
  | { readonly kind: 'changes' }
  | { readonly kind: 'first' }
  | { readonly kind: 'replace-original' }
  | { readonly kind: 'replace'; readonly liveTemplateId: string };

export function publishOutcome(state: ActiveEditorState): PublishOutcome {
  if (state.publicStatus === 'live') return { kind: 'changes' };
  if (isLegacyDesignLive(state)) return { kind: 'replace-original' };
  if (state.live !== null) return { kind: 'replace', liveTemplateId: state.live.templateId };
  return { kind: 'first' };
}

export interface PublishConfirmCopy {
  readonly title: string;
  readonly body: string;
}

/**
 * The question of the publish dialog. `name` is this template's name;
 * `liveName` is the name of the template that is live now, or null when it
 * is not known (the sentence then says "the design live now").
 * `storePublic: false` (the store row says the store is not published) makes
 * a first publish say that shoppers see the design after the store is
 * published; unknown keeps the plain sentence.
 */
export function publishConfirmCopy(
  t: WebsiteT,
  outcome: PublishOutcome,
  name: string,
  liveName: string | null,
  storePublic?: boolean,
): PublishConfirmCopy {
  switch (outcome.kind) {
    case 'changes':
      return {
        title: t('admin.websiteTemplates.publish.titleChanges', { name }),
        body: t('admin.websiteTemplates.publish.bodyChanges', { name }),
      };
    case 'replace-original':
      return {
        title: t('admin.websiteTemplates.publish.titleOriginal'),
        body: t('admin.websiteTemplates.publish.bodyReplaceOriginal'),
      };
    case 'replace':
      return {
        title: t('admin.websiteTemplates.publish.titleDesign', { name }),
        body:
          liveName === null
            ? t('admin.websiteTemplates.publish.bodyReplaceUnknown', { name })
            : t('admin.websiteTemplates.publish.bodyReplace', { name, live: liveName }),
      };
    case 'first':
      return {
        title: t('admin.websiteTemplates.publish.titleDesign', { name }),
        body:
          storePublic === false
            ? t('admin.websiteTemplates.publish.bodyFirstStoreOffline', { name })
            : t('admin.websiteTemplates.publish.bodyFirst', { name }),
      };
  }
}
